import { ChangeDetectionStrategy, Component, ErrorHandler, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core'

import { Asiakas } from '../../_jaettu-lemonator/model/asiakas'
import {
  EmailLiite,
  EmailLiiteDownloadRequest,
  EmailLiiteDownloadResponse,
  VeroilmoitusEmailLahetys,
  VeroilmoitusEmailLahetysHistoryPdfRequest,
  VeroilmoitusEmailLahetysHistoryPdfResponse,
  VirallinenRaporttiName,
} from '../../_jaettu-lemonator/model/kirjanpito'

import { DateService } from '@lemon/core/service/date.service'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { KirjanpitajaService } from '../../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { KirjanpitoUriService } from '../../_jaettu-lemonator/service/kirjanpito-uri.service'

import { Observable, Subject, combineLatest, firstValueFrom, of as observableOf } from 'rxjs'
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { VeroilmoituksenPerustiedot } from './veroilmoitus.component'

interface VeroilmoitusLahetysHistoria extends VeroilmoitusEmailLahetys {
  recipientsEmails: string
  liitteetPromise: Promise<EmailLiite[]>
  nimi: string
  luotuDateStr: string
}

@Component({
  selector: '[app-veroilmoitus-aikaisemmin-lahetetyt-spostit]',
  templateUrl: './veroilmoitus-aikaisemmin-lahetetyt-spostit.component.html',
  styleUrls: ['./veroilmoitus-aikaisemmin-lahetetyt-spostit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class KirjanpitoVeroilmoitusAikaisemminLahetetytComponent implements OnInit, OnDestroy {
  @Input() perustiedotObservable: Observable<VeroilmoituksenPerustiedot>
  @Output() onkoSpostejaLahetetty: EventEmitter<boolean> = new EventEmitter()

  private _asiakasObservable: Observable<Asiakas>
  private _ngUnsubscribe = new Subject<void>()

  sentThisTilikausiObservable: Observable<VeroilmoitusLahetysHistoria[]>

  constructor(
    private _asiakasService: AsiakasService,
    private _dateService: DateService,
    private _ladataanService: LadataanService,
    private _errorHandler: ErrorHandler,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _firebase: FirebaseLemonator,
    private _fileSaverService: FileSaverService,
  ) {}

  ngOnInit() {
    this._asiakasObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      distinctUntilChanged((a, b) => {
        return a?.avain === b?.avain
      }),
    )

    this.sentThisTilikausiObservable = combineLatest([this.perustiedotObservable, this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable]).pipe(
      switchMap(([perustiedot, kirjanpitajatMap]) => {
        if (perustiedot?.asiakas && perustiedot?.tilikausi && kirjanpitajatMap) {
          const uri = this._kirjanpitoUriService.annaVeroilmoitusEmailLahetyksetCollection(perustiedot.asiakas.avain)
          return this._firebase
            .firestoreCollection<VeroilmoitusEmailLahetys>(uri)
            .where('tunniste', '==', this._dateService.localDateToNumber(perustiedot.tilikausi.loppuu) + '_veroilmoitus')
            .listen()
            .pipe(
              map(lahetykset => {
                const historyEntries: VeroilmoitusLahetysHistoria[] = []
                for (const lahetys of lahetykset) {
                  const kirjanpitajaData = kirjanpitajatMap.get(lahetys.luoja)
                  const historyEntry: VeroilmoitusLahetysHistoria = {
                    avain: lahetys.avain,
                    kuukausi: lahetys.kuukausi,
                    tunniste: lahetys.tunniste,
                    luoja: lahetys.luoja,
                    luotu: lahetys.luotu,
                    lahetetty: lahetys.lahetetty,
                    recipients: lahetys.recipients,
                    kieli: lahetys.kieli,
                    aihe: lahetys.aihe,
                    teksti: lahetys.teksti,
                    liitaTuloslaskelma: lahetys.liitaTuloslaskelma,
                    liitaVirallinenTuloslaskelma: lahetys.liitaVirallinenTuloslaskelma,
                    liitaVirallinenTase: lahetys.liitaVirallinenTase,
                    liitaVeroilmoitus: lahetys.liitaVeroilmoitus,
                    liitaTaseErittely: lahetys.liitaTaseErittely,
                    taseErittely: lahetys.taseErittely,
                    veroilmoitusAvain: lahetys.veroilmoitusAvain,
                    veroilmoitus: lahetys.veroilmoitus,
                    lisaaPiilokopio: lahetys.lisaaPiilokopio,
                    isHolviClient: lahetys.isHolviClient,
                    recipientsEmails: lahetys.recipients.reduce((a, b) => a + b.email + ' ', ''),
                    nimi: kirjanpitajaData.etunimi + ' ' + kirjanpitajaData.sukunimi,
                    luotuDateStr: this._dateService.muotoilePaivaJaAikaDate(this._dateService.timestampToDate(lahetys.luotu), 'fi'),
                    liitteetPromise: null,
                  }
                  const liitteetUri = this._kirjanpitoUriService.annaVeroilmoitusEmailLahetyksenLiitteetCollection(perustiedot.asiakas.avain, historyEntry.avain)
                  historyEntry.liitteetPromise = this._firebase.firestoreCollection<EmailLiite>(liitteetUri).where('poistettu', '==', false).get()
                  historyEntries.push(historyEntry)
                }

                return historyEntries.sort((a, b) => b.luotu.toMillis() - a.luotu.toMillis())
              }),
            )
        }
        return observableOf<VeroilmoitusLahetysHistoria[]>(null as VeroilmoitusLahetysHistoria[])
      }),
      tap(data => {
        this.onkoSpostejaLahetetty.emit(data?.length > 0)
      }),
    )
  }

  async downloadSentReport(lahetysAvain: string, reportType: VirallinenRaporttiName) {
    try {
      this._ladataanService.aloitaLataaminen()

      const asiakas = await firstValueFrom(this._asiakasObservable)
      if (!asiakas) {
        return
      }

      const downloadRequest: VeroilmoitusEmailLahetysHistoryPdfRequest = {
        asiakasAvain: asiakas.avain,
        reportType: reportType,
        lahetysAvain: lahetysAvain,
      }
      await this._firebase.functionsCall<VeroilmoitusEmailLahetysHistoryPdfRequest, VeroilmoitusEmailLahetysHistoryPdfResponse>('veroilmoitusEmailLahetysDownloadPdf', downloadRequest).then(resp => {
        if (!resp || resp.e) {
          throw new Error('Failed to download attachment! ' + resp?.e || 'No response')
        }
        if (!resp.base64File) {
          throw new Error('Base64 missing!')
        }
        const filename = asiakas.nimi + '_' + reportType + '_' + lahetysAvain + '.pdf'
        this._fileSaverService.saveBase64As(resp.base64File, filename, 'pdf')
      })
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async downloadAttachment(liite: EmailLiite) {
    try {
      this._ladataanService.aloitaLataaminen()

      const asiakas = await firstValueFrom(this._asiakasObservable)
      if (!asiakas) {
        return
      }

      const downloadRequest: EmailLiiteDownloadRequest = {
        asiakasAvain: asiakas.avain,
        liite: liite,
      }
      this._firebase.functionsCall<EmailLiiteDownloadRequest, EmailLiiteDownloadResponse>('kirjanpitoDownloadEmailLiite', downloadRequest).then(resp => {
        if (!resp || resp.e) {
          throw new Error('Failed to download attachment! ' + resp?.e || 'No response')
        }
        if (!resp.base64File) {
          throw new Error('Base64 missing!')
        }
        this._fileSaverService.saveBase64AsGuessedType(resp.base64File, liite.nimi)
      })
    } catch (error) {
      this._errorHandler.handleError(error)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
